import type { TBlockConfig } from '@conversed/shared/src/central/types/block.type';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  getBlocksApi,
  getFlowMenuEntriesApi,
  getLLMBlocksApi,
  getLLMBlocksByCompanyApi,
  putBlocksApi,
} from '../../../api/flow.api';
import { Actions } from '../../../constants/actionTypes';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../../helpers/toast';
import { mainActions } from '../../../redux/reducers/mainHandler';
import { useMainDispatch } from '../../useMainDispatch';
import { useMainSelector } from '../../useMainSelector';
import { useBlocksUpdates } from './useBlocksUpdates';

export const useBlocksActions = () => {
  const dispatch = useMainDispatch();
  const newDispatch = useDispatch();
  const { blockUpdates } = useBlocksUpdates();
  const {
    designer: { mainRevisionDetail },
  } = useMainSelector();

  const fetchFlowMenuEntries = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      const { data: blocks } = await getFlowMenuEntriesApi();

      dispatch({
        type: Actions.SET_DESIGNER,
        payload: { menuEntries: blocks },
      });
      return 'success';
    } catch (error) {
      console.error(error);
      return 'error';
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch]);

  const getSubFlowsForBlocks = useCallback((blocks: TBlockConfig[]) => {
    const subFlows: string[] = ['main'];

    if (!blocks) return subFlows;
    blocks.forEach((block) => {
      if (!block.subFlow) {
        return;
      }

      if (subFlows.includes(block.subFlow)) {
        return;
      }

      subFlows.push(block.subFlow);
    });

    return subFlows;
  }, []);

  const getBlocks = useCallback(
    async (revisionId?: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        const { data: blocks } = await getBlocksApi(revisionId);
        const subFlows = getSubFlowsForBlocks(blocks);
        newDispatch(mainActions.setSubFlowsForFlow(subFlows));
        // * Clear all block updates on refresh
        newDispatch(mainActions.clearBlockUpdates());
        dispatch({ type: Actions.SET_DESIGNER, payload: { blocks } });
        return 'success';
      } catch (error) {
        console.error(error);
        return 'error';
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, getSubFlowsForBlocks, newDispatch],
  );

  const getLLMBlocks = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      const { data } = await getLLMBlocksApi();
      return data;
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
      return [];
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch]);

  const getLLMBlocksByCompany = useCallback(async (companyIds?: string[]) => {
    try {
      const { data } = await getLLMBlocksByCompanyApi(companyIds);
      return data;
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
      return [];
    }
  }, []);

  const saveFlow = useCallback(async () => {
    const revisionId = mainRevisionDetail?._id ?? '';

    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      await putBlocksApi(blockUpdates, revisionId);
      toastSuccessTranslated('flow.save.success');
      dispatch({ type: Actions.BLOCK_UPDATES_CLEAR, payload: undefined });
    } catch (error) {
      toastErrorTranslated(error);
      console.error(error);
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch, blockUpdates, mainRevisionDetail?._id]);

  return {
    getBlocks,
    saveFlow,
    fetchFlowMenuEntries,
    getLLMBlocks,
    getLLMBlocksByCompany,
  };
};
